import React, { useState, useEffect } from 'react';
import { View, FlatList, TextInput, StyleSheet, TouchableOpacity, KeyboardAvoidingView, Platform, Image, Keyboard, Alert, Linking, Clipboard, Modal, ActivityIndicator } from 'react-native';
import { useRoute, RouteProp, useNavigation } from '@react-navigation/native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import { RootStackParamList } from '../types/navigation';
import { useTheme } from '../theme/ThemeContext';
import { useChat } from '../context/ChatContext';
import { useAuth } from '../context/AuthContext';
import ThemedText from '../components/common/ThemedText';
import LinkText from '../components/common/LinkText';
import ChatMessageSkeleton from '../components/common/ChatMessageSkeleton';
import { apiBaseUrl } from '../config/env';

type PersonalChatRouteProp = RouteProp<RootStackParamList, 'PersonalChat'>;

const hexToRgba = (hex: string, alpha = 1) => {
  const sanitized = hex.replace('#', '');
  const normalized = sanitized.length === 3
    ? sanitized.split('').map((char) => char + char).join('')
    : sanitized;

  const intValue = parseInt(normalized, 16);
  const r = (intValue >> 16) & 255;
  const g = (intValue >> 8) & 255;
  const b = intValue & 255;

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const PersonalChatScreen: React.FC = () => {
  const navigation = useNavigation();
  const route = useRoute<PersonalChatRouteProp>();
  const { theme, isDark } = useTheme();
  const { user } = useAuth();
  const insets = useSafeAreaInsets();
  const { userId: otherUserId, userName, conversationId: initialConversationId, initialMessage } = route.params;
  const { currentMessages, loading, fetchMessages, sendMessage, createConversation, markAsRead, clearMessages } = useChat();
  
  // Much darker gold for dark theme, light gold for light theme
  const ownMessageColor = isDark ? '#4A3A1F' : hexToRgba(theme.primary, 0.15);

  const [messageText, setMessageText] = useState('');
  const [conversationId, setConversationId] = useState<string | undefined>(initialConversationId);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const [showMessageOptions, setShowMessageOptions] = useState(false);
  const [showMenuOptions, setShowMenuOptions] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<typeof currentMessages[number] | null>(null);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });
  const [isBlocked, setIsBlocked] = useState(false);
  const [isBlockedBy, setIsBlockedBy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState<{ uri: string; width: number; height: number } | null>(null);
  const [showAttachmentMenu, setShowAttachmentMenu] = useState(false);
  const [fullscreenImage, setFullscreenImage] = useState<string | null>(null);

  useEffect(() => {
    if (Platform.OS === 'android') {
      const showSubscription = Keyboard.addListener('keyboardDidShow', (e) => {
        setKeyboardHeight(e.endCoordinates.height);
      });
      const hideSubscription = Keyboard.addListener('keyboardDidHide', () => {
        setKeyboardHeight(0);
      });

      return () => {
        showSubscription.remove();
        hideSubscription.remove();
      };
    }
  }, []);

  useEffect(() => {
    // Clear messages when opening a new conversation
    clearMessages();
    initializeConversation();
  }, []);

  useEffect(() => {
    console.log('🔍 [PersonalChat] SafeAreaInsets:', {
      top: insets.top,
      bottom: insets.bottom,
      left: insets.left,
      right: insets.right,
      calculatedPadding: Math.max(12, insets.bottom)
    });
  }, [insets]);

  const initializeConversation = async () => {
    try {
      setLoadingConversation(true);
      
      if (initialConversationId) {
        // Load existing conversation and get blocked status
        const { isBlocked: blocked, isBlockedBy: blockedBy } = await fetchMessages(initialConversationId);
        setIsBlocked(blocked);
        setIsBlockedBy(blockedBy);
        await markAsRead(initialConversationId);
        setConversationId(initialConversationId);
        // Prefill message if provided (e.g., from product/contract details)
        if (initialMessage) {
          setMessageText(initialMessage);
        }
      } else {
        // Check if a conversation already exists with this user by creating one
        // The backend will return existing conversation if it exists
        try {
          const conversation = await createConversation(otherUserId);
          setConversationId(conversation.id);
          
          // Load messages for this conversation
          const { isBlocked: blocked, isBlockedBy: blockedBy } = await fetchMessages(conversation.id);
          setIsBlocked(blocked);
          setIsBlockedBy(blockedBy);
          await markAsRead(conversation.id);
          
          // Prefill message with initial message (will be used for new conversations)
          if (initialMessage) {
            setMessageText(initialMessage);
          }
        } catch (error) {
          console.error('Error creating conversation:', error);
          // If creation failed, still prefill the message for when user sends first message
          if (initialMessage) {
            setMessageText(initialMessage);
          }
        }
      }
    } catch (error) {
      console.error('Error initializing conversation:', error);
    } finally {
      setLoadingConversation(false);
    }
  };

  const handleBack = () => {
    navigation.goBack();
  };

  const handleSendMessage = async () => {
    const trimmedMessage = messageText.trim();
    if (!trimmedMessage || isSending) return;

    setIsSending(true);
    const tempMessageText = trimmedMessage;
    setMessageText(''); // Clear input immediately for better UX

    try {
      // If no conversation exists yet, create it first
      if (!conversationId) {
        const conversation = await createConversation(otherUserId);
        setConversationId(conversation.id);
        await sendMessage(conversation.id, tempMessageText);
      } else {
        await sendMessage(conversationId, tempMessageText);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      // Restore message text on error
      setMessageText(tempMessageText);
      Alert.alert('Error', 'Failed to send message. Please try again.');
    } finally {
      setIsSending(false);
    }
  };

  const handleMessageLongPress = (message: typeof currentMessages[number], event: any) => {
    setSelectedMessage(message);
    // Get touch position
    const { pageY } = event.nativeEvent;
    setMenuPosition({ x: 0, y: pageY - 50 }); // Position above the touch
    setShowMessageOptions(true);
  };

  const handleCopyMessage = () => {
    if (selectedMessage) {
      Clipboard.setString(selectedMessage.content);
    }
    setShowMessageOptions(false);
    setSelectedMessage(null);
  };

  const handleDeleteMessage = async (messageId: string) => {
    Alert.alert(
      'Delete Message',
      'Are you sure you want to delete this message?',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const token = await AsyncStorage.getItem('@lnb_token');
              const response = await fetch(
                `${apiBaseUrl}/api/chats/messages/${messageId}`,
                {
                  method: 'DELETE',
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              if (response.ok) {
                // Refresh messages after deletion
                if (conversationId) {
                  await fetchMessages(conversationId);
                }
                Alert.alert('Success', 'Message deleted');
              } else {
                throw new Error('Failed to delete message');
              }
            } catch (error) {
              console.error('Error deleting message:', error);
              Alert.alert('Error', 'Failed to delete message');
            }
          },
        },
      ]
    );
  };

  const handleMenuPress = () => {
    setShowMenuOptions(true);
  };

  const handleDeleteConversation = () => {
    Alert.alert(
      'Delete Conversation',
      'Are you sure? This will delete all messages.',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              if (!conversationId) return;
              
              const token = await AsyncStorage.getItem('@lnb_token');
              const response = await fetch(
                `${apiBaseUrl}/api/chats/conversations/${conversationId}`,
                {
                  method: 'DELETE',
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }
              );

              if (response.ok) {
                Alert.alert('Success', 'Conversation deleted', [
                  { text: 'OK', onPress: () => navigation.goBack() },
                ]);
              } else {
                throw new Error('Failed to delete conversation');
              }
            } catch (error) {
              console.error('Error deleting conversation:', error);
              Alert.alert('Error', 'Failed to delete conversation');
            }
          },
        },
      ]
    );
  };

  const handleBlockUser = () => {
    Alert.alert(
      'Block User',
      `Block ${userName}? They won't be able to message you.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Block',
          style: 'destructive',
          onPress: async () => {
            try {
              const token = await AsyncStorage.getItem('@lnb_token');
              const response = await fetch(
                `${apiBaseUrl}/api/users/block`,
                {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                  },
                  body: JSON.stringify({ userId: otherUserId }),
                }
              );

              if (response.ok) {
                setIsBlocked(true);
                setShowMenuOptions(false);
              } else {
                throw new Error('Failed to block user');
              }
            } catch (error) {
              console.error('Error blocking user:', error);
              Alert.alert('Error', 'Failed to block user');
            }
          },
        },
      ]
    );
  };

  const handleUnblockUser = async () => {
    try {
      const token = await AsyncStorage.getItem('@lnb_token');
      const response = await fetch(
        `${apiBaseUrl}/api/users/unblock`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ userId: otherUserId }),
        }
      );

      if (response.ok) {
        setIsBlocked(false);
      } else {
        throw new Error('Failed to unblock user');
      }
    } catch (error) {
      console.error('Error unblocking user:', error);
      Alert.alert('Error', 'Failed to unblock user');
    }
  };

  const handleAttachPdf = async () => {
    try {
      // Pick a document - Only PDFs allowed
      const result = await DocumentPicker.getDocumentAsync({
        type: 'application/pdf',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];

      // Validate it's actually a PDF
      if (file.mimeType && file.mimeType !== 'application/pdf') {
        Alert.alert('Invalid File Type', 'Only PDF files are allowed. Please select a PDF file.');
        return;
      }

      // Validate file size (10MB limit)
      if (file.size && file.size > 10 * 1024 * 1024) {
        Alert.alert('File Too Large', 'Please select a file smaller than 10MB');
        return;
      }

      setUploading(true);

      // Upload file to backend
      const token = await AsyncStorage.getItem('@lnb_token');
      const formData = new FormData();
      
      // Handle web differently
      if (Platform.OS === 'web') {
        const response = await fetch(file.uri);
        const blob = await response.blob();
        const webFile = new File([blob], file.name, { type: file.mimeType || 'application/octet-stream' });
        formData.append('file', webFile);
      } else {
        formData.append('file', {
          uri: file.uri,
          type: file.mimeType || 'application/octet-stream',
          name: file.name,
        } as any);
      }

      const uploadResponse = await fetch(`${apiBaseUrl}/api/upload/pdf`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!uploadResponse.ok) {
        const errorData = await uploadResponse.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to upload file');
      }

      const uploadData = await uploadResponse.json();

      // Send message with file attachment
      if (!conversationId) {
        const conversation = await createConversation(otherUserId);
        setConversationId(conversation.id);
        await sendMessage(
          conversation.id,
          file.name,
          'pdf',
          uploadData.url,
          file.name,
          file.size
        );
      } else {
        await sendMessage(
          conversationId,
          file.name,
          'pdf',
          uploadData.url,
          file.name,
          file.size
        );
      }
    } catch (error: any) {
      console.error('Error attaching document:', error);
      const errorMessage = error.message || 'Failed to attach document. Please try again.';
      Alert.alert('Error', errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleAttachImage = async () => {
    try {
      // Request permission
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== 'granted') {
        Alert.alert('Permission Required', 'Please grant access to your photos to share images.');
        return;
      }

      // Pick an image
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        quality: 0.8,
      });

      if (result.canceled) {
        return;
      }

      const image = result.assets[0];
      setSelectedImage({
        uri: image.uri,
        width: image.width,
        height: image.height,
      });
    } catch (error) {
      console.error('Error picking image:', error);
      Alert.alert('Error', 'Failed to pick image. Please try again.');
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const handleSendImage = async () => {
    if (!selectedImage) return;

    try {
      setUploading(true);
      console.log('📤 [PersonalChat] Starting image upload...');
      console.log('📤 Image URI:', selectedImage.uri);
      console.log('📤 Platform:', Platform.OS);

      // Upload image to backend
      const token = await AsyncStorage.getItem('@lnb_token');
      const formData = new FormData();
      
      // Get file extension from URI
      const uriParts = selectedImage.uri.split('.');
      const fileType = uriParts[uriParts.length - 1];
      const filename = `chat-image-${Date.now()}.${fileType}`;
      const mimeType = `image/${fileType}`;
      
      console.log('📤 File info:', { filename, mimeType });
      
      // Handle web differently
      if (Platform.OS === 'web') {
        console.log('🌐 Web platform - converting blob to File...');
        const response = await fetch(selectedImage.uri);
        const blob = await response.blob();
        console.log('📦 Blob created:', { size: blob.size, type: blob.type });
        
        // Use blob's actual MIME type if available, otherwise fallback to our guess
        const actualMimeType = blob.type || mimeType;
        const file = new File([blob], filename, { type: actualMimeType });
        console.log('📄 File created:', { name: file.name, size: file.size, type: file.type });
        formData.append('image', file);
      } else {
        formData.append('image', {
          uri: selectedImage.uri,
          type: mimeType,
          name: filename,
        } as any);
      }

      console.log('🚀 Uploading to:', `${apiBaseUrl}/api/upload/chat-image`);
      const uploadResponse = await fetch(`${apiBaseUrl}/api/upload/chat-image`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      console.log('📥 Upload response status:', uploadResponse.status);
      
      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        console.error('❌ Upload failed:', errorText);
        throw new Error('Failed to upload image');
      }

      const uploadData = await uploadResponse.json();
      console.log('✅ Upload successful:', uploadData);

      // Send message with image attachment
      if (!conversationId) {
        const conversation = await createConversation(otherUserId);
        setConversationId(conversation.id);
        await sendMessage(
          conversation.id,
          'Image',
          'image',
          uploadData.url,
          uploadData.filename,
          uploadData.size
        );
      } else {
        await sendMessage(
          conversationId,
          'Image',
          'image',
          uploadData.url,
          uploadData.filename,
          uploadData.size
        );
      }

      // Clear selected image after sending
      setSelectedImage(null);
    } catch (error) {
      console.error('Error sending image:', error);
      Alert.alert('Error', 'Failed to send image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const getRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return 'Just now';
  };

  const renderMessage = ({ item }: { item: typeof currentMessages[number] }) => {
    const isOwn = item.sender.id === user?.userId;
    
    return (
      <View style={[styles.messageContainer, isOwn && styles.messageContainerOwn]}>
        {!isOwn && (
          <View style={[styles.messageAvatar, { backgroundColor: theme.primary }]}>
            {item.sender.photo ? (
              <Image source={{ uri: item.sender.photo }} style={styles.avatarImage} />
            ) : (
              <Ionicons name="person" size={16} color="#000000" />
            )}
          </View>
        )}
        <TouchableOpacity
          onLongPress={(event) => handleMessageLongPress(item, event)}
          activeOpacity={0.7}
          style={[
            styles.messageBubble,
            { backgroundColor: isOwn ? ownMessageColor : theme.card },
            isOwn && styles.messageBubbleOwn,
            item.type === 'image' && styles.imageBubble
          ]}>
          {item.type === 'image' && item.fileUrl ? (
            <TouchableOpacity onPress={() => setFullscreenImage(item.fileUrl!)} activeOpacity={0.9}>
              <Image 
                source={{ uri: item.fileUrl }} 
                style={styles.messageImage}
                resizeMode="cover"
              />
              <ThemedText style={[
                styles.messageTime,
                styles.imageMessageTime,
                { color: isOwn ? (isDark ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.9)') : 'rgba(255,255,255,0.9)' }
              ]}>
                {getRelativeTime(item.createdAt)}
              </ThemedText>
            </TouchableOpacity>
          ) : item.type === 'pdf' && item.fileUrl ? (
            <TouchableOpacity 
              onPress={() => Linking.openURL(item.fileUrl!)}
              style={[
                styles.pdfAttachment,
                { backgroundColor: isOwn ? 'transparent' : theme.backgroundSecondary }
              ]}
              activeOpacity={0.7}
            >
              <View style={[
                styles.pdfIconContainer,
                { backgroundColor: isOwn ? 'rgba(0,0,0,0.08)' : theme.primary + '20' }
              ]}>
                <Ionicons 
                  name="document-text" 
                  size={28} 
                  color={isOwn ? (isDark ? '#FFFFFF' : '#000000') : theme.primary} 
                />
              </View>
              <View style={styles.pdfInfo}>
                <ThemedText style={[
                  styles.pdfFileName,
                  { color: isOwn ? (isDark ? '#FFFFFF' : '#000000') : theme.text }
                ]} numberOfLines={2}>
                  {item.fileName || item.content || 'Document.pdf'}
                </ThemedText>
                <View style={styles.pdfMeta}>
                  {item.fileSize && (
                    <ThemedText style={[
                      styles.pdfFileSize,
                      { color: isOwn ? (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)') : theme.textSecondary }
                    ]}>
                      {(item.fileSize / 1024).toFixed(1)} KB
                    </ThemedText>
                  )}
                  <ThemedText style={[
                    styles.pdfTapHint,
                    { color: isOwn ? (isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.5)') : theme.textTertiary }
                  ]}>
                    • Tap to open
                  </ThemedText>
                </View>
              </View>
              <Ionicons 
                name="chevron-forward" 
                size={20} 
                color={isOwn ? (isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)') : theme.textTertiary} 
              />
            </TouchableOpacity>
          ) : (
            <>
              <LinkText
                style={[
                  styles.messageText,
                  { color: isOwn ? (isDark ? '#FFFFFF' : '#000000') : theme.text }
                ]}
                linkColor={isOwn ? (isDark ? '#93C5FD' : '#1D4ED8') : '#4A9EFF'}
              >
                {item.content}
              </LinkText>
              <ThemedText style={[
                styles.messageTime,
                { color: isOwn ? (isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)') : theme.textTertiary }
              ]}>
                {getRelativeTime(item.createdAt)}
              </ThemedText>
            </>
          )}
        </TouchableOpacity>
        {isOwn && (
          <View style={[styles.messageAvatar, { backgroundColor: theme.primary }]}>
            {user?.photo ? (
              <Image source={{ uri: user.photo }} style={styles.avatarImage} />
            ) : (
              <Ionicons name="person" size={16} color="#000000" />
            )}
          </View>
        )}
      </View>
    );
  };

  const renderEmptyState = () => (
    <View style={[styles.emptyState, Platform.OS !== 'web' && { transform: [{ scaleY: -1 }] }]}>
      <Ionicons name="chatbubble-outline" size={48} color={theme.textTertiary} />
      <ThemedText style={[styles.emptyStateText, { color: theme.textSecondary }]}>No messages yet</ThemedText>
      <ThemedText style={[styles.emptyStateSubtext, { color: theme.textTertiary }]}>Start the conversation</ThemedText>
    </View>
  );

  const Wrapper = Platform.OS === 'ios' ? KeyboardAvoidingView : View;
  const wrapperProps = Platform.OS === 'ios' 
    ? { behavior: 'padding' as const, keyboardVerticalOffset: 0 }
    : {};

  return (
    <Wrapper
      style={[
        { flex: 1, backgroundColor: theme.background, paddingBottom: Platform.OS === 'android' ? keyboardHeight : 0 },
        Platform.OS === 'web' ? styles.webWrapper : undefined,
      ]}
      {...wrapperProps}
    >
      <SafeAreaView style={[styles.container, { backgroundColor: theme.background }, Platform.OS === 'web' ? styles.webContainer : undefined]} edges={['top', 'left', 'right']}>
        <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }, Platform.OS === 'web' ? styles.webHeader : undefined]}>
          <TouchableOpacity onPress={handleBack} style={styles.headerButton}>
            <Ionicons name="arrow-back" size={28} color={theme.text} />
          </TouchableOpacity>
          <TouchableOpacity 
            style={styles.headerInfo}
            onPress={() => (navigation as any).navigate('Profile', { userId: otherUserId })}
            activeOpacity={0.7}
          >
            <ThemedText style={[styles.headerTitle, { color: theme.text }]}>{userName}</ThemedText>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleMenuPress} style={styles.headerButton}>
            <Ionicons name="ellipsis-vertical" size={24} color={theme.text} />
          </TouchableOpacity>
        </View>

        {/* Blocked User Banner */}
        {(isBlocked || isBlockedBy) && (
          <View style={[styles.blockedBanner, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
            <Ionicons name="ban" size={18} color={theme.textSecondary} />
            <ThemedText style={[styles.blockedText, { color: theme.textSecondary }]}>
              {isBlocked ? 'You blocked this user' : 'This user has blocked you'}
            </ThemedText>
            {isBlocked && (
              <TouchableOpacity 
                onPress={handleUnblockUser}
                style={[styles.unblockButton, { backgroundColor: theme.primary }]}
              >
                <ThemedText style={styles.unblockButtonText}>Unblock</ThemedText>
              </TouchableOpacity>
            )}
          </View>
        )}

        <View style={[styles.messagesViewport, Platform.OS === 'web' ? styles.webMessagesViewport : undefined]}>
          {loadingConversation ? (
            <View style={styles.loadingContainer}>
              <ChatMessageSkeleton />
              <ChatMessageSkeleton />
              <ChatMessageSkeleton />
              <ChatMessageSkeleton />
            </View>
          ) : (
            <FlatList
              data={Platform.OS === 'web' ? [...currentMessages].reverse() : currentMessages}
              keyExtractor={(item) => item.id}
              renderItem={renderMessage}
              contentContainerStyle={[
                styles.messagesList,
                currentMessages.length === 0 && styles.messagesListEmpty,
              ]}
              style={[
                styles.messagesListView,
                Platform.OS === 'web' ? styles.webList : undefined,
                (isBlocked || isBlockedBy) ? { marginBottom: Math.max(16, insets.bottom) } : undefined,
              ]}
              showsVerticalScrollIndicator={false}
              inverted={Platform.OS !== 'web'}
              ListEmptyComponent={renderEmptyState}
            />
          )}
        </View>

        {!isBlocked && !isBlockedBy && (
          <View style={[styles.inputContainer, { backgroundColor: theme.backgroundSecondary, borderTopColor: theme.border, paddingBottom: 12 + insets.bottom }, Platform.OS === 'web' ? styles.webInputContainer : undefined]}>
            {selectedImage ? (
              <View style={styles.imagePreviewContainer}>
                <View style={styles.imagePreviewWrapper}>
                  <Image source={{ uri: selectedImage.uri }} style={styles.imagePreview} resizeMode="cover" />
                  <TouchableOpacity
                    style={[styles.removeImageButton, { backgroundColor: theme.background }]}
                    onPress={handleRemoveImage}
                    activeOpacity={0.7}
                  >
                    <Ionicons name="close" size={20} color={theme.text} />
                  </TouchableOpacity>
                </View>
                <TouchableOpacity
                  style={[styles.sendImageButton, { backgroundColor: theme.primary }]}
                  onPress={handleSendImage}
                  disabled={uploading}
                  activeOpacity={0.7}
                >
                  {uploading ? (
                    <ActivityIndicator size="small" color="#000000" />
                  ) : (
                    <>
                      <Ionicons name="send" size={20} color="#000000" />
                      <ThemedText style={styles.sendImageText}>Send Image</ThemedText>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            ) : (
              <>
                <TouchableOpacity
                  style={[styles.attachButton, { backgroundColor: theme.background, borderWidth: 1, borderColor: theme.border }]}
                  onPress={() => setShowAttachmentMenu(true)}
                  disabled={uploading}
                  activeOpacity={0.7}
                >
                  {uploading ? (
                    <ActivityIndicator size="small" color={theme.primary} />
                  ) : (
                    <Ionicons name="add" size={24} color={theme.text} />
                  )}
                </TouchableOpacity>
                <TextInput
                  style={[styles.input, { backgroundColor: theme.background, color: theme.text, borderWidth: 1, borderColor: theme.border }]}
                  placeholder="Mail"
                  value={messageText}
                  onChangeText={setMessageText}
                  multiline
                  maxLength={500}
                  placeholderTextColor={theme.textSecondary}
                />
                <TouchableOpacity
                  style={[
                    styles.sendButton,
                    { backgroundColor: messageText.trim() && !isSending ? theme.primary : theme.disabled }
                  ]}
                  onPress={handleSendMessage}
                  disabled={!messageText.trim() || isSending}
                  activeOpacity={0.7}
                >
                  <Ionicons name="send" size={20} color="#000000" />
                </TouchableOpacity>
              </>
            )}
          </View>
        )}
      </SafeAreaView>

      {/* Fullscreen Image Viewer */}
      <Modal
        visible={!!fullscreenImage}
        transparent
        animationType="fade"
        onRequestClose={() => setFullscreenImage(null)}
      >
        <View style={styles.fullscreenImageContainer}>
          <TouchableOpacity 
            style={styles.fullscreenImageOverlay}
            activeOpacity={1}
            onPress={() => setFullscreenImage(null)}
          >
            <TouchableOpacity 
              style={styles.closeFullscreenButton}
              onPress={() => setFullscreenImage(null)}
              activeOpacity={0.7}
            >
              <Ionicons name="close" size={30} color="#FFFFFF" />
            </TouchableOpacity>
            {fullscreenImage && (
              <Image 
                source={{ uri: fullscreenImage }} 
                style={styles.fullscreenImage}
                resizeMode="contain"
              />
            )}
          </TouchableOpacity>
        </View>
      </Modal>

      {/* Attachment Menu */}
      <Modal
        visible={showAttachmentMenu}
        transparent
        animationType="fade"
        onRequestClose={() => setShowAttachmentMenu(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowAttachmentMenu(false)}
        >
          <View style={[styles.attachmentMenu, { backgroundColor: theme.card }]}>
            <TouchableOpacity
              style={styles.attachmentMenuItem}
              onPress={() => {
                setShowAttachmentMenu(false);
                handleAttachImage();
              }}
            >
              <View style={[styles.attachmentMenuIconContainer, { backgroundColor: theme.primary + '20' }]}>
                <Ionicons name="image" size={24} color={theme.primary} />
              </View>
              <ThemedText style={[styles.attachmentMenuText, { color: theme.text }]}>Photo</ThemedText>
            </TouchableOpacity>
            
            <View style={[styles.menuDivider, { backgroundColor: theme.border }]} />
            
            <TouchableOpacity
              style={styles.attachmentMenuItem}
              onPress={() => {
                setShowAttachmentMenu(false);
                handleAttachPdf();
              }}
            >
              <View style={[styles.attachmentMenuIconContainer, { backgroundColor: theme.textSecondary + '20' }]}>
                <Ionicons name="document-attach" size={24} color={theme.textSecondary} />
              </View>
              <ThemedText style={[styles.attachmentMenuText, { color: theme.text }]}>Document</ThemedText>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Message Options Context Menu */}
      <Modal
        visible={showMessageOptions}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMessageOptions(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay} 
          activeOpacity={1}
          onPress={() => setShowMessageOptions(false)}
        >
          <View style={[
            styles.contextMenu, 
            { 
              backgroundColor: theme.card, 
              top: menuPosition.y,
              right: selectedMessage?.sender.id === user?.userId ? 60 : undefined,
              left: selectedMessage?.sender.id === user?.userId ? undefined : 60,
            }
          ]}>
            <TouchableOpacity
              style={styles.menuItem}
              onPress={handleCopyMessage}
            >
              <Ionicons name="copy-outline" size={18} color={theme.text} />
              <ThemedText style={[styles.menuText, { color: theme.text }]}>Copy</ThemedText>
            </TouchableOpacity>
            
            {selectedMessage?.sender.id === user?.userId && (
              <>
                <View style={[styles.menuDivider, { backgroundColor: theme.border }]} />
                <TouchableOpacity
                  style={styles.menuItem}
                  onPress={() => {
                    setShowMessageOptions(false);
                    if (selectedMessage) {
                      handleDeleteMessage(selectedMessage.id);
                    }
                  }}
                >
                  <Ionicons name="trash-outline" size={18} color="#FF3B30" />
                  <ThemedText style={[styles.menuText, { color: '#FF3B30' }]}>Delete</ThemedText>
                </TouchableOpacity>
              </>
            )}
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Menu Options Context Menu */}
      <Modal
        visible={showMenuOptions}
        transparent
        animationType="fade"
        onRequestClose={() => setShowMenuOptions(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay} 
          activeOpacity={1}
          onPress={() => setShowMenuOptions(false)}
        >
          <View style={[styles.contextMenu, { backgroundColor: theme.card, top: 60, right: 16 }]}>
            <TouchableOpacity
              style={styles.menuItem}
              onPress={() => {
                setShowMenuOptions(false);
                handleDeleteConversation();
              }}
            >
              <Ionicons name="trash-outline" size={18} color="#FF3B30" />
              <ThemedText style={[styles.menuText, { color: '#FF3B30' }]}>Delete Chat</ThemedText>
            </TouchableOpacity>
            
            <View style={[styles.menuDivider, { backgroundColor: theme.border }]} />
            
            <TouchableOpacity
              style={styles.menuItem}
              onPress={() => {
                setShowMenuOptions(false);
                handleBlockUser();
              }}
            >
              <Ionicons name="ban-outline" size={18} color="#FF3B30" />
              <ThemedText style={[styles.menuText, { color: '#FF3B30' }]}>
                {isBlocked ? 'Unblock' : 'Block'}
              </ThemedText>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </Wrapper>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerInfo: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  messagesList: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  messagesListEmpty: {
    flexGrow: 1,
    justifyContent: 'center',
  },
  messageContainer: {
    flexDirection: 'row',
    marginBottom: 12,
    alignItems: 'flex-end',
  },
  messageContainerOwn: {
    flexDirection: 'row-reverse',
  },
  messageAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    justifyContent: 'center',
    alignItems: 'center',
  },
  messageBubble: {
    borderRadius: 16,
    padding: 12,
    marginHorizontal: 8,
    maxWidth: '70%',
  },
  messageBubbleOwn: {
    maxWidth: '85%',
  },
  messageText: {
    fontSize: 15,
    lineHeight: 20,
  },
  messageTime: {
    fontSize: 11,
    marginTop: 4,
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  attachButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
  },
  input: {
    flex: 1,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    fontSize: 15,
    maxHeight: 100,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8,
  },
  pdfAttachment: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 12,
    marginBottom: 4,
    minWidth: 240,
    maxWidth: '100%',
  },
  pdfIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  pdfInfo: {
    flex: 1,
    marginRight: 8,
  },
  pdfFileName: {
    fontSize: 15,
    fontWeight: '600',
    lineHeight: 20,
    marginBottom: 4,
  },
  pdfMeta: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  pdfFileSize: {
    fontSize: 13,
    fontWeight: '500',
  },
  pdfTapHint: {
    fontSize: 12,
  },
  emptyState: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyStateText: {
    fontSize: 16,
    marginTop: 12,
  },
  emptyStateSubtext: {
    fontSize: 14,
    marginTop: 4,
  },
  webWrapper: {
    height: '100%',
    minHeight: 0,
  },
  webContainer: {
    height: '100%',
    minHeight: 0,
  },
  messagesViewport: {
    flex: 1,
    minHeight: 0,
  },
  webMessagesViewport: {
    overflow: 'hidden',
  },
  messagesListView: {
    flex: 1,
  },
  webHeader: {
    flexShrink: 0,
  },
  webList: {
    flex: 1,
    minHeight: 0,
  },
  webInputContainer: {
    flexShrink: 0,
  },
  avatarImage: {
    width: 28,
    height: 28,
    borderRadius: 14,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  blockedBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 8,
    borderBottomWidth: 1,
  },
  blockedText: {
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  unblockButton: {
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 6,
  },
  unblockButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
  },
  contextMenu: {
    position: 'absolute',
    minWidth: 140,
    borderRadius: 12,
    paddingVertical: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 10,
  },
  menuText: {
    fontSize: 15,
    fontWeight: '500',
  },
  menuDivider: {
    height: 1,
    marginHorizontal: 8,
  },
  fullscreenImageContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
  },
  fullscreenImageOverlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  fullscreenImage: {
    width: '100%',
    height: '100%',
  },
  closeFullscreenButton: {
    position: 'absolute',
    top: 50,
    right: 20,
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  attachmentMenu: {
    position: 'absolute',
    bottom: 100,
    left: 20,
    right: 20,
    borderRadius: 16,
    paddingVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 12,
    elevation: 8,
  },
  attachmentMenuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 20,
    gap: 16,
  },
  attachmentMenuIconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
  },
  attachmentMenuText: {
    fontSize: 17,
    fontWeight: '600',
  },
  imagePreviewContainer: {
    width: '100%',
    gap: 12,
  },
  imagePreviewWrapper: {
    position: 'relative',
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
  },
  imagePreview: {
    width: '100%',
    height: 200,
    borderRadius: 12,
  },
  removeImageButton: {
    position: 'absolute',
    top: 8,
    right: 8,
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  sendImageButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 12,
    gap: 8,
  },
  sendImageText: {
    color: '#000000',
    fontSize: 16,
    fontWeight: '600',
  },
  imageBubble: {
    padding: 4,
    maxWidth: '85%',
  },
  messageImage: {
    width: 240,
    height: 180,
    borderRadius: 12,
  },
  imageMessageTime: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
  },
});

export default PersonalChatScreen;
